import type {
  ActionPlan,
  Task,
} from '@getaccept/dsr-shared-new/src/mutual-action-plan/types/action-plan';
import type { Ref } from 'vue';
import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import MilestoneConstants from '@getaccept/dsr-shared-new/src/api/mutual-action-plan/constants/load-milestone.constant';
import type { LoadMilestoneDto } from '@getaccept/dsr-shared-new/src/api/mutual-action-plan/types/load-milestone-dto';
import { TaskStatus } from '@getaccept/dsr-shared-new/src/mutual-action-plan/types/enums/task-status';
import { t } from '@getaccept/lib-shared-new/src/helpers/translation.helper';
import { useToast } from '@getaccept/lib-shared-new/src/toast';
import type { Channel } from 'pusher-js';
import { DealroomEvent } from '@getaccept/pusher';
import { ActionPlanHelper } from '@getaccept/dsr-shared-new/src/mutual-action-plan/helpers/action-plan.helper';
import { ActionPlanService } from '../../../api/mutual-action-plan/services/action-plan.service';
import { pusherClient } from '../../../api/pusher/pusher-client';

export const usePublishedActionPlanStore = defineStore('publishedActionPlan', () => {
  const loadingMilestones = ref(false);
  const loadingMore = ref(false);
  const error = ref(false);
  const actionPlans: Ref<ActionPlan[]> = ref([]);

  const load = async (showLoading = true) => {
    try {
      const loadedActionPlans = await ActionPlanService.load();
      actionPlans.value = loadedActionPlans;

      await Promise.all(
        actionPlans.value.map(plan =>
          loadMilestones(
            {
              ...MilestoneConstants.defaultFilter,
              actionPlanId: plan.id,
            },
            showLoading
          )
        )
      );
    } catch (e) {
      error.value = true;
    }
  };

  const loadMilestones = async (payload: LoadMilestoneDto, showLoading = true) => {
    if (showLoading) {
      payload.offset > 0 ? (loadingMore.value = true) : (loadingMilestones.value = true);
    }
    try {
      const loadedMilestones = await ActionPlanService.loadMilestones(payload);

      actionPlans.value = ActionPlanHelper.handleLoadedMilestones(
        actionPlans.value,
        loadedMilestones,
        payload
      );
    } catch (e) {
      error.value = true;
    } finally {
      loadingMilestones.value = false;
      loadingMore.value = false;
    }
  };

  const updateTaskStatus = async (
    actionPlanId: string,
    milestoneId: string,
    taskId: string,
    status: TaskStatus,
    participantId: string
  ) => {
    try {
      await ActionPlanService.updateTaskStatus(actionPlanId, milestoneId, taskId, status);

      actionPlans.value = ActionPlanHelper.updateTask({
        actionPlans: actionPlans.value,
        actionPlanId,
        milestoneId,
        task: { status, statusUpdatedBy: participantId } as Task,
        taskId,
      });
      if (status === TaskStatus.Completed) {
        useToast().toast.success(t('MAP_toaster_description'));
      }
    } catch (e) {
      error.value = true;
    }
  };

  const deleteMilestone = ({
    actionPlanId,
    milestoneId,
  }: {
    actionPlanId: string;
    milestoneId: string;
  }) => {
    actionPlans.value = ActionPlanHelper.deleteMilestone({
      actionPlans: actionPlans.value,
      actionPlanId,
      milestoneId,
    });
  };

  const deleteTask = ({
    actionPlanId,
    milestoneId,
    taskId,
  }: {
    actionPlanId: string;
    milestoneId: string;
    taskId: string;
  }) => {
    actionPlans.value = ActionPlanHelper.deleteTask({
      actionPlans: actionPlans.value,
      actionPlanId,
      milestoneId,
      taskId,
    });
  };

  const handleUpdateTask = async ({
    actionPlanId,
    milestoneId,
    taskId,
  }: {
    actionPlanId: string;
    milestoneId: string;
    taskId: string;
  }) => {
    try {
      const loadedTask = await ActionPlanService.loadTask(actionPlanId, milestoneId, taskId);

      actionPlans.value = ActionPlanHelper.updateTask({
        actionPlans: actionPlans.value,
        actionPlanId,
        milestoneId,
        task: loadedTask,
        taskId,
      });
    } catch {
      error.value = true;
    }
  };

  const handleAddTask = async ({
    actionPlanId,
    milestoneId,
    taskId,
  }: {
    actionPlanId: string;
    milestoneId: string;
    taskId: string;
  }) => {
    try {
      const loadedTask = await ActionPlanService.loadTask(actionPlanId, milestoneId, taskId);

      actionPlans.value = ActionPlanHelper.addTask({
        actionPlans: actionPlans.value,
        actionPlanId,
        milestoneId,
        task: loadedTask,
        taskId,
      });
    } catch {
      error.value = true;
    }
  };

  const handleUpdateMilestone = async ({
    actionPlanId,
    milestoneId,
  }: {
    actionPlanId: string;
    milestoneId: string;
  }) => {
    try {
      const loadedMilestone = await ActionPlanService.loadMilestone(actionPlanId, milestoneId);

      actionPlans.value = ActionPlanHelper.updateMilestone({
        actionPlans: actionPlans.value,
        actionPlanId,
        milestoneId,
        milestone: loadedMilestone,
      });
    } catch {
      error.value = true;
    }
  };

  const handleAddMilestone = async ({
    actionPlanId,
    milestoneId,
  }: {
    actionPlanId: string;
    milestoneId: string;
  }) => {
    try {
      const loadedMilestone = await ActionPlanService.loadMilestone(actionPlanId, milestoneId);

      actionPlans.value = ActionPlanHelper.addMilestone({
        actionPlans: actionPlans.value,
        actionPlanId,
        milestoneId,
        milestone: loadedMilestone,
      });
    } catch {
      error.value = true;
    }
  };

  const handleUpdateActionPlan = async ({ actionPlanId }: { actionPlanId: string }) => {
    try {
      const loadedActionPlans = await ActionPlanService.load();

      if (!loadedActionPlans || loadedActionPlans.length === 0) {
        actionPlans.value = [];
      }

      const updatedActionPlan = loadedActionPlans.find(plan => plan.id === actionPlanId);
      const loadedMilestones = await ActionPlanService.loadMilestones({
        ...MilestoneConstants.defaultFilter,
        actionPlanId,
      });

      const payload = {
        title: updatedActionPlan.title,
        description: updatedActionPlan.description,
        milestones: loadedMilestones,
      };

      actionPlans.value = ActionPlanHelper.updatePlan({
        actionPlans: loadedActionPlans,
        actionPlanId,
        actionPlan: payload,
      });
    } catch {
      error.value = true;
    }
  };

  const addListeners = (channel: Channel) => {
    pusherClient.listen(channel, DealroomEvent.MilestoneCreated, handleAddMilestone);
    pusherClient.listen(channel, DealroomEvent.MilestoneUpdated, handleUpdateMilestone);
    pusherClient.listen(channel, DealroomEvent.TaskCreated, handleAddTask);
    pusherClient.listen(channel, DealroomEvent.TaskUpdated, handleUpdateTask);
    pusherClient.listen(channel, DealroomEvent.MilestoneDeleted, deleteMilestone);
    pusherClient.listen(channel, DealroomEvent.TaskDeleted, deleteTask);
    pusherClient.listen(channel, DealroomEvent.ActionPlanUpdated, handleUpdateActionPlan);
  };

  const actionPlanVisible = computed(() =>
    Boolean(actionPlans.value.length && actionPlans.value[0].id)
  );

  const reset = () => {
    error.value = false;
    actionPlans.value = [];
  };

  return {
    load,
    loadMilestones,
    reset,
    updateTaskStatus,
    deleteMilestone,
    deleteTask,
    addListeners,
    handleUpdateMilestone,
    handleAddMilestone,
    handleUpdateActionPlan,
    handleAddTask,
    handleUpdateTask,
    loadingMilestones,
    loadingMore,
    error,
    actionPlans,
    actionPlanVisible,
  };
});
